import { assertUnreachable, isNotDefined } from '@sgme/fp';

import { getQuote, selectOptionQuoteLegs } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption, LegId, LegsData, PremiumCategory, PremiumData, PremiumType } from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';
import { roundToFixed } from '@/utils/number.ts';

export interface Premium {
  bid: number;
  bidIsNegative: boolean;
  ask: number;
  askIsNegative: boolean;
  isReversed: boolean;
}

export function formatPremium(value: number, premiumCategory: PremiumCategory, currencyPairPrecision: number): number {
  switch (premiumCategory) {
    case 'PERCENT':
      return value * 100;
    case 'PPS':
      return value * 10 ** (currencyPairPrecision - 1);
    default:
      return value;
  }
}

export function parsePremium(value: number, premiumCategory: PremiumCategory, currencyPairPrecision: number): number {
  switch (premiumCategory) {
    case 'PPS':
      return convertPointsToRate(value, currencyPairPrecision - 1, 10);
    case 'PERCENT':
      return roundToFixed(value / 100, 10);
    case 'AMOUNT':
    case 'VOLATILITY':
      return value;
  }
}

export function getAssociatedPremiumCategory(premiumCategory: PremiumCategory): PremiumCategory {
  switch (premiumCategory) {
    case 'AMOUNT':
    case 'VOLATILITY':
      return 'PERCENT';
    case 'PERCENT':
    case 'PPS':
      return 'AMOUNT';
  }
}
export function getPremiumFromQuote(premiumType: PremiumType, option: FxOption, legId: LegId): Premium {
  const quote = getQuote(option);
  if (isNotDefined(quote)) {
    return { bid: 0, ask: 0, isReversed: false, askIsNegative: false, bidIsNegative: false };
  }

  const legsData = selectOptionQuoteLegs(option, legId);
  const typeCurrencyIndex = premiumType.premiumCurrency;

  const premium = typeCurrencyIndex === 1 ? quote.ccy1 : quote.ccy2;
  const volatility = legsData?.marketData?.volatility;

  const premiumCategory = premiumType.premiumCategory;
  const premiumAmount = normalizePremium({ bid: premium.bidAmount, ask: premium.askAmount });

  switch (premiumCategory) {
    case 'VOLATILITY':
      if (isNotDefined(volatility)) {
        return { bid: 0, ask: 0, isReversed: false, askIsNegative: false, bidIsNegative: false };
      }
      return normalizeRelativePremium({ bid: volatility?.bid, ask: volatility?.ask }, premiumAmount);
    case 'AMOUNT':
      return premiumAmount;
    case 'PERCENT':
      return normalizeRelativePremium({ bid: premium.bidPercent, ask: premium.askPercent }, premiumAmount);
    case 'PPS':
      return normalizeRelativePremium({ bid: premium.bidPps, ask: premium.askPps }, premiumAmount);
    default:
      assertUnreachable(premiumCategory, `Unknown premium type: ${premiumType}`);
  }
}

export function getPremiumFromQuoteLegs(legData: LegsData | undefined, premiumType: PremiumType): Premium | undefined {
  const premiumData = legData?.premiumData;
  if (isNotDefined(premiumData)) {
    return undefined;
  }

  const amountKeys = getPremiumQuoteKey({ ...premiumType, premiumCategory: 'AMOUNT' });
  const premiumAmount = normalizePremium({ bid: premiumData[amountKeys.bidKey], ask: premiumData[amountKeys.askKey] });

  const { bidKey, askKey } = getPremiumQuoteKey(premiumType);
  return normalizeRelativePremium({ bid: premiumData[bidKey], ask: premiumData[askKey] }, premiumAmount);
}

function getPremiumQuoteKey(premiumType: PremiumType): { bidKey: keyof PremiumData; askKey: keyof PremiumData } {
  switch (premiumType.premiumCategory) {
    case 'AMOUNT':
    case 'VOLATILITY':
      return {
        bidKey: `bidAmountCcy${premiumType.premiumCurrency}`,
        askKey: `askAmountCcy${premiumType.premiumCurrency}`,
      };
    case 'PERCENT':
      return {
        bidKey: `bidPercentCcy${premiumType.premiumCurrency}`,
        askKey: `askPercentCcy${premiumType.premiumCurrency}`,
      };
    case 'PPS':
      return { bidKey: `bidPpsCcy${premiumType.premiumCurrency}`, askKey: `askPpsCcy${premiumType.premiumCurrency}` };
  }
}

function normalizePremium(amount: BidAsk<number>): Premium {
  const isReversed = amount.bid * -1 > amount.ask;
  const askIsNegative = amount.ask < 0;
  const bidIsNegative = amount.bid < 0;
  return isReversed
    ? {
        bid: Math.abs(amount.ask),
        ask: Math.abs(amount.bid),
        isReversed: true,
        askIsNegative: bidIsNegative,
        bidIsNegative: askIsNegative,
      }
    : { bid: Math.abs(amount.bid), ask: Math.abs(amount.ask), isReversed: false, askIsNegative, bidIsNegative };
}

function normalizeRelativePremium(
  amount: BidAsk<number>,
  { askIsNegative, bidIsNegative, isReversed }: Premium,
): Premium {
  return isReversed
    ? {
        bid: Math.abs(amount.ask),
        ask: Math.abs(amount.bid),
        isReversed: true,
        askIsNegative: bidIsNegative,
        bidIsNegative: askIsNegative,
      }
    : { bid: Math.abs(amount.bid), ask: Math.abs(amount.ask), isReversed: false, askIsNegative, bidIsNegative };
}

export function getPremiumSuffix(premiumCategory: PremiumCategory, currency: string) {
  switch (premiumCategory) {
    case 'AMOUNT':
      return ` ${currency}`;
    case 'PERCENT':
      return ` % ${currency}`;
    case 'PPS':
      return ' Pips';
    case 'VOLATILITY':
      return '';
    default:
      assertUnreachable(premiumCategory, `Unknown premium category: ${premiumCategory}`);
  }
}

function convertPointsToRate(value: number, ccyPrecision: number, roundingPrecision = 10): number {
  const factor = 10 ** ccyPrecision;
  return roundToFixed(value / factor, roundingPrecision);
}
